import {
	type VideoRecorderStartFormSchema,
	videoRecorderStartFormSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { resolver } from "../libs/react-hook-form";
import { useTranslation } from "../localization";
import { Modal } from "../modal";
import { RadioGrid } from "../radio-grid";
import { toast } from "../toast";
import {
	CameraUnavailableError,
	MicrophoneUnavailableError,
	type RecordingTarget,
} from "./recorder-session";
import { isCameraBubbleSupported } from "./camera-bubble";
import { useVideoRecorder } from "./use-video-recorder";

/** Props Interface */
interface VideoRecorderStartModalProps {
	/**
	 * Creates the destination the recording will belong to — for the wiki that means saving the
	 * page, creating the video row and anchoring its placeholder block — and answers where to
	 * ship it. Returning null abandons the attempt without starting a capture, which is what a
	 * failed save must do: the author is about to walk away from this page.
	 */
	onPrepare: () => Promise<RecordingTarget | null>;
	/**
	 * Called once a capture is genuinely running. The host uses it to step out of the way — and
	 * it must not fire earlier, because a host that closes itself while the picker is still open
	 * takes this modal down with it and leaves the failure nowhere to be shown.
	 */
	onStarted?: () => void;
}

export function VideoRecorderStartModal({ onPrepare, onStarted }: VideoRecorderStartModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Store */
	const controller = useVideoRecorder();

	const methods = useForm({
		resolver: resolver(videoRecorderStartFormSchema),
		defaultValues: {
			type: "screen",
		},
	});

	/** Functions */
	async function handleOnStartRecording(json: VideoRecorderStartFormSchema) {
		// The capture opens first and the destination is prepared inside it — see
		// `startRecording`. Preparing first spent the click's activation on network calls and
		// pushed the surface picker outside the window it needs.
		let started = false;
		try {
			started = await controller.onStartRecording(json.type, onPrepare);
		} catch (error) {
			// A dismissed surface picker is a decision, not a failure, and stays silent. Anything
			// else is worth saying: an expired activation, a blocked permissions policy or a
			// refused device all look identical to the author otherwise — the modal simply sits
			// there and nothing happens.
			console.error("Starting the recording failed:", error);
			if (error instanceof MicrophoneUnavailableError) {
				toast.error(t("video_recorder.microphone_unavailable"));
			} else if (error instanceof CameraUnavailableError) {
				toast.error(t("video_recorder.camera_unavailable"));
			} else if (!(error instanceof DOMException && error.name === "NotAllowedError")) {
				toast.error(t("video_recorder.start_failed"));
			}
			return;
		}
		if (!started) return;
		controller.disclosure.onClose();
		onStarted?.();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={controller.disclosure.isOpen}
				onClose={controller.disclosure.onClose}
				title={t("video_recorder.capture_video")}
				width="40rem"
				onSubmit={methods.handleSubmit(handleOnStartRecording)}
			>
				<Modal.Body>
					{/* Ordered by what is added, not by what is most used: the picture alone, then
					    a voice over it, then a face, then both. Each name says what it records,
					    which is the whole of what there is to know here. */}
					<RadioGrid
						control={methods.control}
						name="type"
					>
						<RadioGrid.Item
							iconName="Monitor"
							value="screen-silent"
							title={t("video_recorder.screen_silent")}
						/>
						<RadioGrid.Item
							iconName="Mic"
							value="screen"
							title={t("video_recorder.screen_with_voice")}
						/>
						<RadioGrid.Item
							iconName="Camera"
							value="camera"
							title={t("video_recorder.camera_with_voice")}
						/>
						{/* Hidden rather than disabled where the browser cannot compose: the option
						    would explain nothing about why it refuses, and the three that remain
						    are what that browser can actually do. */}
						{isCameraBubbleSupported() && (
							<RadioGrid.Item
								iconName="PictureInPicture"
								value="screen-camera"
								title={t("video_recorder.screen_with_camera")}
							/>
						)}
					</RadioGrid>
				</Modal.Body>
				<Modal.FooterForm submitLabel={t("video_recorder.start_recording")} />
			</Modal>
		</FormProvider>
	);
}
